import {
  asyncGeocodeByType,
  isInRangeOf,
  sortByNearestTo
} from '../../../utils/locationUtils'

import { onEvent } from '../../../utils/eventUtils'

const Constants = Object.freeze({
  CenterOfUnitedStatesLatLng: Object.freeze({
    lat: 41.850033,
    lng: -87.6500523
  })
})

//
// Initialization
// --------------
//
// Initialize support for searching locations that are
// These are functions initialize the backbone that powers our location search.
//

function initSearchForm() {
  if (searchForm !== null && locationButton !== null) {
    searchForm.addEventListener('submit', handleSearch)
    locationButton.addEventListener('click', handleCurrentLocation)
  }

  onEvent(document.body, 'click', '.locations-list-item', (ev, targetEl) => {
    const detail = toLocationObjectFromDOMNode(targetEl)
    dispatchOriginChanged(detail.latLng)
  })
}

//
// DOM Snapshots
// -------------
//
// Values captured during initialization.

const searchForm = document.querySelector('#location-search-form')
const list = document.querySelectorAll('.locations-list-item')
let listContainer = document.querySelector('.locations-list')

//
// Event Dispatchers
// -----------------
//

/**
 * Dispatches an event that instructs the map to change the center lat/lng and
 * also the zoom. If the latLng you pass is falsey, then we re-center to the US
 * and zoom out a bit. This is handy for handling search terms that don't match
 * on any results.
 *
 * @param {*} latLng
 * @returns {void}
 */
function dispatchOriginChanged(latLng) {
  const changeEvent = new CustomEvent('origin:changed', {
    detail: {
      latLng: !latLng ? Constants.CenterOfUnitedStatesLatLng : latLng,
      zoom: !latLng ? 3 : 8
    }
  })

  document.dispatchEvent(changeEvent)
}

//
// Location List
// -------------
//
// List of locations in the DOM that we copy immediately. This should hold all
// locations despite whatever we have displayed in the UI.
if(listContainer !== null){
  const clonedListOfLocations = listContainer.cloneNode(true)
  window.clonedListOfLocations = clonedListOfLocations
}

//
// Selectors/Accessors
// -------------------
//

const getLocationSearchInput = () => document.getElementById('location-search')

const toLocationObjectFromDOMNode = el => ({
  id: el.dataset.id,
  lat: Number(el.dataset.latitude),
  lng: Number(el.dataset.longitude),
  name: el.dataset.locationName,
  cloneOfNode: clonedListOfLocations.querySelector(`#${el.id}`).cloneNode(true)
})

function getLocationObjects() {
  return Array.from(list).map(toLocationObjectFromDOMNode)
}

//
// DOM Mutators
// ------------
//

/**
 * Places some error text above the list of locations so the user knows their
 * search has no nearby locations, or that their device's geocoding services are
 * disabled.
 *
 * @param {string} searchType either 'address' or 'latLng' (i.e., searching by
 *    the user's current position).
 * @param {*} shouldReplaceChildren a boolean indicating whether to reset the
 *    list of locations (defaults to true).
 */
function showErrorMessageAboveLocations(
  searchType,
  shouldReplaceChildren = true
) {
  let pTag = document.createElement('p')
  pTag.className = 'location-search-error'

  if (searchType.type === 'address') {
    pTag.innerText = `Oops, no locations found for "${searchType.term}"`
  } else if (searchType.type === 'latLng') {
    pTag.innerText = 'No locations found near you'
  } else {
    pTag.innerText = 'Unable to find any locations'
  }

  if (shouldReplaceChildren) {
    const nextChildren = clonedListOfLocations.cloneNode(true).children
    listContainer.replaceChildren(...nextChildren)
  }
  listContainer.prepend(pTag, document.createElement('hr'))
}

function clearSearchError() {
  listContainer
    .querySelectorAll('.location-search-error')
    .forEach(el => el.remove())
}

/**
 * Handles a search event emitted by the search form itself.
 *

 * @param {*} event
 * @returns
 */
async function handleSearch(event) {
  event.preventDefault()
  clearSearchError()

  const term = getLocationSearchInput()
    .value.toLowerCase()
    .trim()

  const params = { type: 'address', term }

  try {
    const geocodeResult = await asyncGeocodeByType('address', term)
    if (!geocodeResult.ok) {
      console.error(geocodeResult.error)
      return showErrorMessageAboveLocations(params)
    }

    return searchWithOrigin(geocodeResult.latLng, params)
  } catch (error) {
    console.error(error)
    showErrorMessageAboveLocations(params)
  }
}

/**
 * Performs the actual search and ranking of locations using the given latitude
 * and longitude. If an error occurs, or if there aren't any locations that fall
 * within the user's search term, then we will use `searchType` to display a
 * proper error.
 *
 * @param {object} latLng object holding the latitude and longitude of the
 *    search's origin location.
 * @param {object} searchType object with the property type, and additional
 *    properties. Usually of the shape `{type: 'term', term: 'user input!'}`.
 * @returns {void}
 */
async function searchWithOrigin(latLng, searchType) {
  const nearest = sortByNearestTo(latLng, getLocationObjects())
  listContainer.replaceChildren(...nearest.map(el => el.cloneOfNode))

  const isOneInRange = isInRangeOf(150, latLng, nearest[0])
  dispatchOriginChanged(isOneInRange && nearest[0])

  // We'll re-center the map regardless of whether a store is within range.
  // After re-centering, we'll display an error (and all locations)
  if (!isOneInRange) {
    // We'll still re-center the map, but show an error so the user can go on
    // their merry way.
    showErrorMessageAboveLocations(searchType, false)
    return
  }

  const nearest25 = nearest.slice(0, 25)
  await injectDistancesForFirst25(latLng, nearest25)
}

/**
 * Uses the Distances API to get the estimated driving distance for the top 25
 * and populates the HTML.
 *
 * @param {object} latLng latitude longitude instance or plain object
 * @param {array} nearest25 the nearest 25 locations for the user
 */
async function injectDistancesForFirst25(latLng, nearest25) {
  const distanceResults = await getDistances(latLng, nearest25)
  const originRows = distanceResults && distanceResults.rows
  const relevantElements = originRows && originRows[0] && originRows[0].elements

  for (let i = 0; i < relevantElements.length; i++) {
    const relevantElement = relevantElements[i]
    const matchingElement = nearest25[i]

    if (!matchingElement) {
      break
    }

    const item = listContainer.querySelector(`#location-${matchingElement.id}`)

    if (!item) {
      continue
    }

    // We shouldn't use an id here, since it's not truly unique.
    const blockWithDistance = document.querySelector(
      `#location-${matchingElement.id} #location-distance`
    )
    if (!blockWithDistance) {
      continue
    }

    blockWithDistance
    blockWithDistance.style.display = 'block'
    blockWithDistance.innerText = relevantElement.distance.text
  }
}

//sort results by distance

function getDistances(origin, destinations) {
  const service = new google.maps.DistanceMatrixService()

  return new Promise((resolve, reject) => {
    service.getDistanceMatrix(
      {
        origins: [origin],
        destinations,
        travelMode: 'DRIVING',
        unitSystem: google.maps.UnitSystem.IMPERIAL
      },
      (data, status) => {
        if (status !== 'OK') {
          console.error(data)
          // Data is actually an error in this case.
          reject(data)
          return
        }

        resolve(data)
      }
    )
  })
}

//current location button
const locationButton = document.querySelector('.locations-search__geo-btn')

function lookupSuccessful(position) {
  const { latitude, longitude } = position.coords
  window.localStorage.setItem('latitude', latitude)
  window.localStorage.setItem('longitude', longitude)

  searchWithOrigin({ lat: latitude, lng: longitude }, { type: 'latLng' })

  asyncGeocodeByType('latLng', { lat: latitude, lng: longitude })
    .then(result => {
      if (result.ok) {
        const formattedAddress = result.first && result.first.formatted_address
        getLocationSearchInput().value = formattedAddress
      }
    })
    .catch(error => {
      console.error(error)
      showErrorMessageAboveLocations('latLng')
    })
}

function lookupFailed(error) {
  console.error(error)
}

function handleCurrentLocation(event) {
  event.preventDefault()
  if (window.navigator.geolocation) {
    window.navigator.geolocation.getCurrentPosition(
      lookupSuccessful,
      lookupFailed,
      { maximumAge: 60000, timeout: 10000 }
    )
  }
}

function init() {
  initSearchForm()
}

export default init
export const search = {searchWithOrigin}



// WEBPACK FOOTER //
// ./frontend/resources/assets/js/components/ui/global/locationSearch.js